'use strict';

import moment from 'moment';
import Alerts from 'lib/alerts';
import EmployerBaseRouter from './Base.js';
import SettingsLayoutView from '../views/settings/Layout.js';
import Log from 'lib/log';
import Positions from 'newCollections/positions.js';
import CompanyLocations from 'newCollections/company-locations.js';
import Payroll from 'newModels/payroll.js';
import Integration from 'newModels/integration.js';
import PayrollItemIntegrations
    from 'newCollections/payroll-item-integrations.js';
import LeaveTypes from 'newCollections/leave-types.js';
import Payrolls from 'newCollections/payrolls';
import Coupon from 'newModels/coupon';

const log = Log('employer/settings-router');

module.exports = EmployerBaseRouter.extend({
    routes: {
        'settings':               'settings',
        'settings/company':       'company',
        'settings/tax':           'tax',
        'settings/positions':     'positions',
        'settings/payrollItems':  'payrollItems',
        'settings/leave':         'leave',
        'settings/payment':       'payment',
        'settings/notifications': 'notifications',
        'settings/subscription':  'subscription',
        'settings/integrations':  'integrations'
    },

    titles: {
        'settings':               'Settings',
        'settings/company':       'Settings > Company Details',
        'settings/tax':           'Settings > Tax Settings',
        'settings/positions':     'Settings > Positions',
        'settings/payrollItems':  'Settings > Payroll Items',
        'settings/leave':         'Settings > Leave',
        'settings/payment':       'Settings > Payment',
        'settings/notifications': 'Settings > Notifications',
        'settings/subscription':  'Settings > Subscription',
        'settings/integrations':  'Settings > Integrations'
    },

    initialize: function(options) {
        this.options = _.defaults(options || {}, {log: log});
        EmployerBaseRouter.prototype.initialize.apply(this, [this.options]);

        this.layout = new SettingsLayoutView({
            el: this.el
        });
    },

    settings: function() {
        this.navigate('settings/company', {trigger: true, replace: true});
    },

    company: function() {
        this.layout.renderTab('company');
        app.company.fetchBankAccount()
            .then(() => app.company.fetchLocations())
            .then(locations => locations.fetchAddresses())
            .then(locations => locations.fetchEmployees())
            .then(locations => {
                this.layout.renderContent({
                    model: app.company,
                    locations
                });
            })
            .catch(err => { Alerts.show('criticalError', err); })
            .finally(() => this.layout.hideLoader());
    },

    tax: function() {
        this.layout.renderTab('tax');
        app.user
            .fetch()
            .then(model => {
                this.layout.renderContent({
                    model
                });
                this.layout.hideLoader()
            });
    },

    positions: function() {
        this.layout.renderTab('positions');

        const positions = new Positions(null, {
            company: app.company,
            pagination: false,
            filter: {
                where: {hiddenAt: null},
                limit: 9999
            }
        });

        positions.fetch().then(() => {
                this.layout.renderContent({
                    collection: positions,
                    company:    app.company
                });
            })
            .catch(err => Alerts.show('criticalError', err))
            .finally(() => this.layout.hideLoader());
    },

    payrollItems: function() {
        this.layout.renderTab('payrollItems');

        return Payroll.fetchCurrent(app.company)
            .then(unsubmittedPayroll => {
                return this.layout.renderContent({
                    collection: app.payrollItems,
                    unsubmittedPayroll: unsubmittedPayroll
                });
            })
            .catch(err => Alerts.show('criticalError', err))
            .finally(() => this.layout.hideLoader());
    },

    leave: function() {
        this.layout.renderTab('leave');

        let leaveTypes = new LeaveTypes(null, {company: app.company});

        return Promise.all([
            Payroll.fetchCurrent(app.company),
            leaveTypes.fetch()
        ]).then(([unsubmittedPayroll]) => {
            this.layout.renderContent({
                model: app.company,
                unsubmittedPayroll: unsubmittedPayroll,
                leaveTypes: leaveTypes.getActiveTypes()
            });
        })
        .catch(err => Alerts.show('criticalError', err))
        .finally(() => this.layout.hideLoader());
    },

    payment: function() {
        this.layout.renderTab('payment');

        const locations = new CompanyLocations(null, {
            company: app.company,
            pagination: false,
            syncRemove: true
        });

        return Promise.all([
            app.company.fetchCurrentPayroll(),
            locations.fetch()
        ]).then(([unsubmittedPayroll, locations]) => {
            this.layout.renderContent({
                model: app.company,
                unsubmittedPayroll,
                locations
            });
        })
        .catch(err => Alerts.show('criticalError', err))
        .finally(() => this.layout.hideLoader());
    },

    notifications: function() {
        this.layout.renderTab('notifications');
        this.layout.renderContent({model: app.company});
        this.layout.hideLoader();
    },

    subscription: function() {
        this.layout.renderTab('subscription');
        if (
            !app.subscription ||
            (app.company && app.company.get('mppAdminId'))
        ) {
            this.layout.renderContent({
                model: null,
                templateExtra: {
                    mppCustomer: !!app.company.get('mppAdminId'),
                    invoices: []
                }
            });
            this.layout.hideLoader();
            return;
        }

        const fetchYearlyStartDate = () => {
            const payrolls = new Payrolls(null, {
                company: app.company,
                pagination: false,
                sortKey: 'periodDate',
                sortOrder: 'ascending',
            });
            return payrolls.fetch({
                filter: {
                    where: {
                        status: {
                            eq: 9
                        },
                        chargebeeInvoiceId: (
                            app.subscription.get('yearlyInvoice') || {}
                        ).id || null
                    },
                    limit: 1
                }
            }).then(() => {
                const firstPayroll = payrolls.at(0);
                // yearlyStartDate
                return firstPayroll ? firstPayroll.get('periodDate') : null;
            });
        };

        return Promise.all([
            app.subscription.listInvoices(),
            (app.subscription.get('yearlyInvoice') || {}).id ?
                fetchYearlyStartDate() : null
        ]).then(([data, yearlyStartDate]) => {
            const invoices = (data || []).map(i => {
                let description = '-';
                let status = i.status.replace(/_/, ' ');
                status = status[0].toUpperCase() + status.slice(1);
                /*jscs:disable requireCamelCaseOrUpperCaseIdentifiers*/
                if (i.line_items && i.line_items.length) {
                    /*jscs:disable requireCamelCaseOrUpperCaseIdentifiers*/
                    description = i.line_items[0].description;
                }
                return {
                    id: i._id,
                    date: moment.unix(i.date).format('DD/MM/YYYY'),
                    description: description,
                    amount: (i.total / 100).formatWithoutRounding(),
                    status: status,
                    paymentDate: i.paid_at ?
                        moment.unix(i.paid_at).format('DD/MM/YYYY') : '',
                    voidedDate: i.voided_at ?
                        moment.unix(i.voided_at).format('DD/MM/YYYY') : ''
                };
            });
            const hasVoidInvoice = app.subscription.get('plan') === 'client' &&
                data.some(d => d.status === 'voided');
            const updateStatusPromise = hasVoidInvoice ?
                app.subscription.updateStatus() :
                Promise.resolve(null);
            return Promise.all([
                Promise.resolve(invoices),
                yearlyStartDate,
                app.user.isAdmin() ? Coupon.getDiscountCodes() : [],
                updateStatusPromise
            ]);
        }).then(([invoices, yearlyStartDate, discountCodes]) => {
            this.layout.renderContent({
                model: app.subscription,
                templateExtra: {
                    invoices: invoices,
                    yearlyStartDate: yearlyStartDate || null,
                    discountCodes
                }
            });
        }).finally(() => this.layout.hideLoader());
    },

    integrations: function() {
        this.layout.renderTab('integrations');

        return Integration.fetchIntegration(app.company).then(integration => {
            const provider = integration.get('provider');

            if (provider === 'quickbooks') {
                return Promise.all([
                    PayrollItemIntegrations.fetchPaymentAccountsForCompany(
                        app.company, integration
                    ),
                    PayrollItemIntegrations.fetchForCompany(
                        app.company, integration
                    ),
                    integration.fetchData()
                ]).then(([paymentAccounts, mapped, qbAccounts]) => {
                    let mappables = app.payrollItems.getMappable();
                    let defaults = app.payrollItems.getMappableDefaults();

                    if (mapped.length === 0) {
                        mapped.add(defaults.map(item => {
                            return {
                                payrollItemId: item.id,
                                companyId: app.company.id,
                                integrationId: integration.id,
                                remoteId: null,
                                syncedAt: null
                            };
                        }));
                    }

                    this.layout.renderContent({
                        model: integration,
                        mappedItems: mapped,
                        mappableItems: mappables,
                        provider,
                        paymentAccounts,
                        defaults,
                        qbAccounts
                    });
                });
            } else if (provider === 'xero') {
                return Promise.all([
                    PayrollItemIntegrations.fetchForCompany(
                        app.company, integration, true
                    ),
                    integration.fetchData()
                ])
                .then(([
                    items, formData
                ]) => {
                    let mappableItems = app.payrollItems.getMappable();

                    const mappingTypes = [
                        {id: 'net', label: 'Net salary'},
                        {id: 'base', label: 'Basic salary'},
                        {id: 'gross', label: 'Gross salary'},
                    ];

                    this.layout.renderContent({
                        provider,
                        model: integration,
                        mappingTypes,
                        items,
                        mappableItems,
                        organisations: formData.organisations,
                        contacts: formData.contacts,
                        debitAccounts: formData.debitAccounts,
                        creditAccounts: formData.creditAccounts
                    });
                })
                .catch(() => {
                    this.layout.renderContent({model: null});

                    const title = 'Xero connection error';
                    const message = `Could not access Xero API, ` +
                        `please reconnect or contact your system administrator.`;
                    Alerts.show('error', {
                        title,
                        html: message
                    });
                });
            } else {
                this.layout.renderContent({
                    provider: 'unknown',
                    model: null,
                });
            }
        })
        .catch(() => {
            this.layout.renderContent({model: null});
        })
        .finally(() => this.layout.hideLoader());
    }
});



// WEBPACK FOOTER //
// ./app/employer/routers/Settings.js